import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/ui/loading-skeleton";
import { Archive, Eye, AlertCircle, CheckCircle, Package } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Order } from "@/hooks/useOrdersManagement/types";

interface OrderWithTimestamp extends Order {
  created_at: string;
}

interface ArchivedOrdersProps {
  onViewProducts: (order: OrderWithTimestamp) => void;
}

export const ArchivedOrders = ({ onViewProducts }: ArchivedOrdersProps) => {
  const { data: archivedOrders = [], isLoading, error } = useQuery<OrderWithTimestamp[]>({
    queryKey: ['archived-orders'],
    queryFn: async () => {
      console.log('📦 Fetching archived orders...');
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('status', 'archived')
        .order('updated_at', { ascending: false });
      
      if (error) {
        console.error('❌ Error fetching archived orders:', error);
        throw error;
      }
      
      console.log('✅ Archived orders fetched:', data.length);
      return data.map(order => ({
        id: order.id,
        supplierName: order.supplier_name,
        deliveryDate: order.delivery_date,
        status: order.status as "active" | "completed" | "archived",
        productCount: order.product_count,
        hasCredit: order.has_credit,
        documents: order.documents || [],
        createdBy: order.created_by,
        created_at: order.created_at
      }));
    }
  });

  return (
    <div className="space-y-6">
      {/* Content */}
      {error ? (
        <Card className="border border-destructive/20">
          <CardContent className="pt-6">
            <div className="text-center py-6">
              <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Archived Orders</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {error.message || "Unable to load archived orders. Please try refreshing the page."}
              </p>
              <Button onClick={() => window.location.reload()} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : !archivedOrders || archivedOrders.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Archive className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Archived Orders</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                When orders are completed and archived, they will appear here for your records.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {archivedOrders.map((order) => (
            <Card 
              key={order.id} 
              className="hover:shadow-md transition-all duration-200 cursor-pointer border border-border/40"
              onClick={() => onViewProducts(order)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base font-semibold truncate">
                        {order.supplierName || 'Unknown Supplier'}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground font-mono">#{order.id}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Archived
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivered:</span>
                    <span className="font-medium">
                      {new Date(order.deliveryDate).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Products:</span>
                    <span className="font-medium">{order.productCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Archived:</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  {order.createdBy && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Added by:</span>
                      <span className="text-xs font-medium">{order.createdBy}</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-end mt-4">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewProducts(order);
                    }}
                    variant="outline"
                    size="sm"
                    className="h-8"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};